/**
 * Pure predicates for frontend version-skew detection.
 *
 * React-free by design so both `hasSkew` and `isSafeToReload` are trivially
 * unit-testable under jsdom without mounting a component tree. Consumed by
 * `useVersionSkewNotice` (hooks/useVersionSkewNotice.ts), which owns the
 * fetch/polling/state-machine side of things.
 */

/** Countdown length (seconds) shown by the production auto-reload banner once skew latches. */
export const RELOAD_COUNTDOWN_SECONDS = 20;

/** Poll cadence while in 'auto-reload' mode (production stations). */
export const AUTO_RELOAD_CHECK_INTERVAL_MS = 5 * 60 * 1000;

/** Poll cadence while in 'notify' mode (inventory app). */
export const NOTIFY_CHECK_INTERVAL_MS = 15 * 60 * 1000;

/** How often the notify-mode toast re-raises once skew is detected (no once-only dead end). */
export const NOTIFY_REPEAT_MS = 10 * 60 * 1000;

/** Rate-limit for re-checking on window focus / visibilitychange. */
export const FOCUS_DEBOUNCE_MS = 60 * 1000;

/**
 * True when the locally-baked build id and the freshly-fetched remote build
 * id are both present and differ. An undefined/empty remote id (mid-deploy
 * 404, malformed payload) is treated as "no skew" — fail closed to today's
 * silent behaviour rather than nagging on a transient blip.
 */
export function hasSkew(localBuildId: string, remoteBuildId: string | undefined): boolean {
  if (!remoteBuildId) return false;
  return remoteBuildId !== localBuildId;
}

/** Git short-SHA convention (7 chars); `dev-…` ids share one prefix, so truncating would render an identical fake-SHA for years — return those in full. */
export function shortBuildId(id: string): string {
  return id.startsWith('dev-') ? id : id.slice(0, 7);
}

/**
 * True when it is safe to force-reload the current document right now.
 *
 * Stations are stateless between scans, so a reload is safe by default. The
 * only cases that block it are ones where a reload would destroy in-flight
 * operator input: an open dialog, an explicit `[data-reload-blocked]`
 * escape hatch, or a non-scanner text field with pending typed content.
 *
 * The scanner-input carve-out is load-bearing: `[data-scanner-input]` is
 * ALWAYS focused by design at an idle station (see PRODUCTION_DESIGN.md
 * §3.1), and it frequently carries a residual value between scans. Treating
 * that as "the operator is typing" would block the reload forever and
 * defeat the entire feature.
 */
function autoReloadGuardKey(remoteBuildId: string): string {
  return `version-skew-reloaded:${remoteBuildId}`;
}

/**
 * True when an auto-reload has ALREADY been attempted for this remote build
 * id — the circuit breaker for the auto-reload mode. Without it, a skew
 * that survives a reload (stale `index.html` from an intermediary cache on
 * the China path serving an older document than `/version.json`) would
 * reload every station on every poll cycle, forever, with no dismiss and no
 * flag — a worse floor outage than the stale bundle itself.
 *
 * Fails CLOSED: if storage is unreachable we report "already fired" so the
 * banner degrades to manual-refresh-only instead of risking a reload loop.
 */
export function hasAutoReloadFiredForBuild(
  storage: Pick<Storage, 'getItem'>,
  remoteBuildId: string
): boolean {
  try {
    return storage.getItem(autoReloadGuardKey(remoteBuildId)) !== null;
  } catch {
    return true;
  }
}

/** Record that an auto-reload is being attempted for this remote build id. */
export function markAutoReloadFiredForBuild(
  storage: Pick<Storage, 'setItem'>,
  remoteBuildId: string
): void {
  try {
    storage.setItem(autoReloadGuardKey(remoteBuildId), '1');
  } catch {
    // Storage unavailable — the peek above already fails closed.
  }
}

export function isSafeToReload(doc: Document): boolean {
  if (doc.hidden) return true;

  if (doc.querySelector('[role="dialog"], [role="alertdialog"]')) return false;
  if (doc.querySelector('[data-reload-blocked]')) return false;

  const active = doc.activeElement;
  if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) {
    const isScannerInput = active.hasAttribute('data-scanner-input');
    if (!isScannerInput && active.value !== '') return false;
  }

  return true;
}
